/**
 * LLM models hook.
 *
 * Fetches the workspace's user-added custom models from `/workspace/models`
 * and shapes them into the `LlmProvider[]` view that the agent builder and
 * chat LLM selector expect. Models whose provider is currently disconnected
 * are filtered out so selectors don't offer unusable options.
 */

import { useCallback, useEffect, useState } from "react";
import type { LlmModel, LlmProvider } from "../services/credit-providers-service";
import {
  workspaceProviderService,
  type ProviderEnum,
  type WorkspaceCustomModel,
} from "../services/workspace-provider-service";

interface UseLlmModelsOptions {
  autoFetch?: boolean;
  pollingInterval?: number;
}

interface LlmModelsLoadingState {
  loading: boolean;
  error: string | null;
  retrying: boolean;
}

interface LlmModelsState {
  providers: LlmProvider[];
  loading: boolean;
  error: string | null;
}

interface UseLlmModelsReturn {
  state: LlmModelsState;
  loading: LlmModelsLoadingState;
  fetchModels: () => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
  getAllModels: () => Array<LlmModel & { provider: string }>;
  findModel: (modelId: string) => (LlmModel & { provider: string }) | undefined;
  /**
   * Looks a model up by its `workspace_custom_models` row id.
   *
   * The workspace Super-Agent model is stored as that UUID rather than a model
   * string, so this is the only way a caller holding workspace settings can
   * reach the model's capabilities.
   */
  findModelByRowId: (rowId: string) => (LlmModel & { provider: string }) | undefined;
}

const PROVIDER_DISPLAY_NAME: Record<ProviderEnum, string> = {
  OPENAI: "OpenAI",
  ANTHROPIC: "Anthropic",
  GROQ: "Groq",
  OPEN_ROUTER: "OpenRouter",
  DEEPSEEK: "DeepSeek",
  CODEX: "Codex CLI",
  ZAI: "Z.AI",
  OLLAMA: "Ollama",
  LM_STUDIO: "LM Studio",
  CLAUDE_CODE: "Claude Code",
  CUSTOM: "OpenAI-compatible",
};

/**
 * Groups a flat list of workspace custom models by provider, producing the
 * existing `LlmProvider[]` shape consumers already render.
 */
function toLlmProviders(rows: WorkspaceCustomModel[]): LlmProvider[] {
  const grouped = new Map<string, LlmModel[]>();

  for (const row of rows) {
    // Hide models whose provider was disconnected — they can't be used.
    if (!row.providerConnected) continue;

    const displayProviderName = PROVIDER_DISPLAY_NAME[row.provider] ?? row.provider;
    const existing = grouped.get(displayProviderName) ?? [];
    existing.push({
      id: row.providerModelId,
      name: row.displayName,
      description: "",
      hasReasoning: row.hasReasoning,
      reasoningOptions: row.reasoningOptions,
      rowId: row.id,
      providerEnum: row.provider,
      capabilities: row.capabilities,
    });
    grouped.set(displayProviderName, existing);
  }

  return Array.from(grouped.entries()).map(([provider, models]) => ({
    provider,
    models,
  }));
}

export function useLlmModels(options: UseLlmModelsOptions = {}): UseLlmModelsReturn {
  const { autoFetch = true, pollingInterval } = options;

  const [state, setState] = useState<LlmModelsState>({
    providers: [],
    loading: false,
    error: null,
  });

  const [loading, setLoading] = useState<LlmModelsLoadingState>({
    loading: false,
    error: null,
    retrying: false,
  });

  const fetchModels = useCallback(async (): Promise<void> => {
    setLoading((prev) => ({ ...prev, loading: true, error: null }));
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const rows = await workspaceProviderService.listCustomModels();
      const providers = toLlmProviders(rows);
      setState({ providers, loading: false, error: null });
      setLoading({ loading: false, error: null, retrying: false });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch LLM models";
      setState((prev) => ({ ...prev, loading: false, error: message }));
      setLoading((prev) => ({ ...prev, loading: false, error: message }));
    }
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    await fetchModels();
  }, [fetchModels]);

  const reset = useCallback((): void => {
    setState({ providers: [], loading: false, error: null });
    setLoading({ loading: false, error: null, retrying: false });
  }, []);

  const getAllModels = useCallback(() => {
    return state.providers.flatMap((provider) =>
      provider.models.map((model) => ({
        ...model,
        provider: provider.provider,
      })),
    );
  }, [state.providers]);

  const findModel = useCallback(
    (modelId: string) => getAllModels().find((model) => model.id === modelId),
    [getAllModels],
  );

  useEffect(() => {
    if (autoFetch) void fetchModels();
  }, [autoFetch, fetchModels]);

  const findModelByRowId = useCallback(
    (rowId: string) => getAllModels().find((model) => model.rowId === rowId),
    [getAllModels],
  );

  useEffect(() => {
    if (!pollingInterval) return;
    const interval = setInterval(() => {
      if (!loading.loading) void refresh();
    }, pollingInterval);
    return () => clearInterval(interval);
  }, [pollingInterval, loading.loading, refresh]);

  return {
    state,
    loading,
    fetchModels,
    refresh,
    reset,
    getAllModels,
    findModel,
    findModelByRowId,
  };
}
